import React, { useState, useEffect, useCallback } from 'react';
import { scrapingService } from '../services/scrapingService';
import { toast } from 'react-hot-toast';
import MultiCountySelect from './MultiCountySelect';

// ============================================
// Reusable UI Components
// ============================================

const StatCard = ({ label, value, sublabel, color = 'text-gray-900', icon }) => (
  <div className="bg-white rounded-lg border shadow-sm p-4">
    <div className="flex items-center justify-between">
      <div className="text-sm text-gray-500">{label}</div>
      {icon && <span className="text-lg">{icon}</span>}
    </div>
    <div className={`mt-1 text-2xl font-bold ${color}`}>{value}</div>
    {sublabel && <div className="text-xs text-gray-400 mt-1">{sublabel}</div>}
  </div>
);

const CostCard = ({ label, amount, breakdown, color = 'text-green-600' }) => (
  <div className="bg-gradient-to-br from-gray-50 to-white rounded-lg border shadow-sm p-4">
    <div className="text-sm text-gray-500 mb-1">{label}</div>
    <div className={`text-2xl font-bold ${color}`}>
      ${typeof amount === 'number' ? amount.toFixed(2) : amount}
    </div>
    {breakdown && <div className="text-xs text-gray-400 mt-1">{breakdown}</div>}
  </div>
);

const ProgressBar = ({ value, max, label, color = 'bg-blue-500' }) => {
  const percentage = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="mb-3">
      <div className="flex justify-between text-xs text-gray-500 mb-1">
        <span>{label}</span>
        <span>{value.toLocaleString()} / {max.toLocaleString()} ({percentage.toFixed(1)}%)</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div className={`${color} h-2 rounded-full transition-all duration-500`} style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
};

const ZbStatusBadge = ({ status, count, total }) => {
  const colorMap = {
    valid: 'bg-green-100 text-green-800 border-green-200',
    invalid: 'bg-red-100 text-red-800 border-red-200',
    catch_all: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    unknown: 'bg-gray-100 text-gray-600 border-gray-200',
    spamtrap: 'bg-red-200 text-red-900 border-red-300',
    abuse: 'bg-orange-100 text-orange-800 border-orange-200',
    do_not_mail: 'bg-orange-100 text-orange-700 border-orange-200',
    other: 'bg-gray-100 text-gray-500 border-gray-200'
  };
  
  const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : 0;
  
  return (
    <div className={`inline-flex items-center px-2 py-1 rounded-md border ${colorMap[status] || colorMap.other} text-xs font-medium mr-2 mb-2`}>
      <span className="capitalize">{status.replace('_', '-')}</span>
      <span className="ml-1 opacity-75">({count} · {percentage}%)</span>
    </div>
  );
};

// ============================================
// County KPI Card Component
// ============================================

const CountyKpiCard = ({ countyName, stats, costs, isLoading }) => {
  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border shadow-sm p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="grid grid-cols-2 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-20 bg-gray-100 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const {
    nonEnrichedCount,
    enrichedCount,
    enrichedWithEmail,
    totalEmailSlots,
    verifiedEmails,
    unverifiedEmails,
    zbStatusBreakdown,
    validEmailRate,
    enrichmentSuccessRate
  } = stats;

  // Calculate costs
  const batchDataCost = enrichedWithEmail * costs.batchDataCostPerCall;
  const zbVerifiedCost = verifiedEmails * costs.zbCostPerEmail;
  const zbRemainingCost = unverifiedEmails * costs.zbCostPerEmail;
  
  // NOTE: nonEnrichedCount is the TOTAL pool (master list), enrichedCount is the subset that's been enriched
  const totalInRange = nonEnrichedCount;
  const pendingEnrichment = Math.max(0, nonEnrichedCount - enrichedCount);
  const enrichmentPct = totalInRange > 0 ? ((enrichedCount / totalInRange) * 100).toFixed(1) : 0;
  
  // Estimated future costs (based on remaining records to enrich)
  const estimatedNewEnrichments = Math.round(pendingEnrichment * (costs.zbSuccessRate / 100));
  const estimatedEnrichmentCost = estimatedNewEnrichments * costs.batchDataCostPerCall;
  // Estimate emails per enrichment (based on current ratio)
  const avgEmailsPerEnrichment = enrichedWithEmail > 0 ? totalEmailSlots / enrichedWithEmail : 1.5;
  const estimatedNewEmails = Math.round(estimatedNewEnrichments * avgEmailsPerEnrichment);
  const estimatedZbCost = estimatedNewEmails * costs.zbCostPerEmail;
  
  // ROI metrics
  const totalInvested = batchDataCost + zbVerifiedCost;
  const validLeads = zbStatusBreakdown.valid || 0;
  const costPerValidLead = validLeads > 0 ? totalInvested / validLeads : 0;

  return (
    <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
        <h3 className="text-lg font-semibold text-white">{countyName}</h3>
        <p className="text-blue-100 text-sm">
          {totalInRange.toLocaleString()} total records in range • {enrichmentPct}% enriched
        </p>
      </div>

      <div className="p-6">
        {/* Record Counts Section */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
            <span className="mr-2">📊</span> Record Statistics
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard 
              label="Total In Range" 
              value={totalInRange.toLocaleString()} 
              color="text-indigo-600"
              icon="📊"
            />
            <StatCard 
              label="Enriched Records" 
              value={enrichedCount.toLocaleString()} 
              sublabel={`${enrichmentPct}% of total`}
              color="text-blue-600"
              icon="✅"
            />
            <StatCard 
              label="Awaiting Enrichment" 
              value={pendingEnrichment.toLocaleString()} 
              color="text-orange-600"
              icon="📋"
            />
            <StatCard 
              label="With Emails" 
              value={enrichedWithEmail.toLocaleString()} 
              sublabel={`${enrichmentSuccessRate}% success rate`}
              color="text-green-600"
              icon="📧"
            />
          </div>
        </div>

        {/* Progress Bars */}
        <div className="mb-6 bg-gray-50 rounded-lg p-4">
          <ProgressBar 
            label="Enrichment Progress" 
            value={enrichedCount} 
            max={totalInRange}
            color="bg-blue-500"
          />
          <ProgressBar 
            label="Email Verification Progress" 
            value={verifiedEmails} 
            max={totalEmailSlots}
            color="bg-green-500"
          />
        </div>

        {/* Email Statistics */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
            <span className="mr-2">📬</span> Email Statistics
          </h4>
          <div className="grid grid-cols-3 gap-3">
            <StatCard 
              label="Total Emails" 
              value={totalEmailSlots.toLocaleString()} 
              color="text-gray-700"
            />
            <StatCard 
              label="Verified (ZB)" 
              value={verifiedEmails.toLocaleString()} 
              color="text-green-600"
            />
            <StatCard 
              label="Pending Verification" 
              value={unverifiedEmails.toLocaleString()} 
              color="text-orange-500"
            />
          </div>
        </div>

        {/* ZeroBounce Status Breakdown */}
        {verifiedEmails > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
              <span className="mr-2">🔍</span> ZeroBounce Status Breakdown
            </h4>
            <div className="flex flex-wrap">
              {Object.entries(zbStatusBreakdown)
                .filter(([_, count]) => count > 0)
                .sort((a, b) => b[1] - a[1])
                .map(([status, count]) => (
                  <ZbStatusBadge key={status} status={status} count={count} total={verifiedEmails} />
                ))
              }
            </div>
            <div className="mt-3 text-sm">
              <span className="text-gray-600">Valid Email Rate: </span>
              <span className={`font-semibold ${validEmailRate >= 50 ? 'text-green-600' : validEmailRate >= 30 ? 'text-yellow-600' : 'text-red-600'}`}>
                {validEmailRate}%
              </span>
              <span className="text-gray-400 ml-2">({validLeads} valid emails)</span>
            </div>
          </div>
        )}

        {/* Cost Analysis Section */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
            <span className="mr-2">💰</span> Cost Analysis (Actual Spend)
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <CostCard 
              label="BatchData Enrichment" 
              amount={batchDataCost}
              breakdown={`${enrichedWithEmail.toLocaleString()} × $${costs.batchDataCostPerCall}`}
              color="text-blue-600"
            />
            <CostCard 
              label="ZeroBounce Verified" 
              amount={zbVerifiedCost}
              breakdown={`${verifiedEmails.toLocaleString()} × $${costs.zbCostPerEmail}`}
              color="text-green-600"
            />
            <CostCard 
              label="Total Invested" 
              amount={totalInvested}
              breakdown="BatchData + ZeroBounce"
              color="text-purple-600"
            />
          </div>
        </div>

        {/* Remaining & Estimated Costs */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
            <span className="mr-2">📈</span> Estimated Remaining Costs
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <CostCard 
              label="ZB Remaining Emails" 
              amount={zbRemainingCost}
              breakdown={`${unverifiedEmails.toLocaleString()} pending × $${costs.zbCostPerEmail}`}
              color="text-orange-500"
            />
            <CostCard 
              label="Est. Future Enrichment" 
              amount={estimatedEnrichmentCost}
              breakdown={`~${estimatedNewEnrichments.toLocaleString()} new records`}
              color="text-blue-500"
            />
            <CostCard 
              label="Est. Future ZB Cost" 
              amount={estimatedZbCost}
              breakdown={`~${estimatedNewEmails.toLocaleString()} new emails`}
              color="text-green-500"
            />
          </div>
        </div>

        {/* ROI Metrics */}
        {validLeads > 0 && (
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-100">
            <h4 className="text-sm font-semibold text-purple-800 mb-2 flex items-center">
              <span className="mr-2">🎯</span> ROI Metrics
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-gray-500">Cost per Valid Lead</div>
                <div className="text-xl font-bold text-purple-700">${costPerValidLead.toFixed(4)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Valid Leads Acquired</div>
                <div className="text-xl font-bold text-green-600">{validLeads.toLocaleString()}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// Main CostAnalyticsPanel Component
// ============================================

const CostAnalyticsPanel = () => {
  // State selectors
  const [states, setStates] = useState([]);
  const [stateId, setStateId] = useState('');
  const [counties, setCounties] = useState([]);
  const [selectedCounties, setSelectedCounties] = useState([]);

  // Filter states
  const [acresFilterMode, setAcresFilterMode] = useState('between');
  const [minAcres, setMinAcres] = useState(50);
  const [maxAcres, setMaxAcres] = useState('');

  // Configurable cost inputs
  const [batchDataCostPerCall, setBatchDataCostPerCall] = useState(0.0025);
  const [zbCostPerEmail, setZbCostPerEmail] = useState(0.0064);
  const [zbSuccessRate, setZbSuccessRate] = useState(60);

  // KPI data per county
  const [countyKpiData, setCountyKpiData] = useState({});
  const [loadingCounties, setLoadingCounties] = useState(new Set());

  // Cost settings object for passing to components
  const costs = {
    batchDataCostPerCall,
    zbCostPerEmail,
    zbSuccessRate
  };

  // Load states on mount
  useEffect(() => {
    (async () => {
      try {
        const s = await scrapingService.fetchStates();
        setStates(Array.isArray(s) ? s : []);
      } catch (e) {
        console.error('Error loading states:', e);
      }
    })();
  }, []);

  // Load counties when state changes
  useEffect(() => {
    (async () => {
      if (!stateId) {
        setCounties([]);
        setSelectedCounties([]);
        setCountyKpiData({});
        return;
      }
      try {
        const c = await scrapingService.fetchCounties(stateId);
        setCounties(Array.isArray(c) ? c : []);
        setSelectedCounties([]);
        setCountyKpiData({});
      } catch (e) {
        console.error('Error loading counties:', e);
      }
    })();
  }, [stateId]);

  // Fetch KPI data for a single county
  const fetchCountyKpi = useCallback(async (county) => {
    const countyId = county.county_id;
    
    setLoadingCounties(prev => new Set([...prev, countyId]));
    
    try {
      const kpiData = await scrapingService.fetchKpiStats(
        countyId,
        minAcres || 0,
        maxAcres || null,
        acresFilterMode
      );
      
      setCountyKpiData(prev => ({
        ...prev,
        [countyId]: kpiData
      }));
    } catch (error) {
      console.error(`Error fetching KPI for ${county.county_name}:`, error);
      toast.error(`Failed to load KPI for ${county.county_name}`);
    } finally {
      setLoadingCounties(prev => {
        const next = new Set(prev);
        next.delete(countyId);
        return next;
      });
    }
  }, [minAcres, maxAcres, acresFilterMode]);

  // Fetch KPI data when counties are selected or filters change
  useEffect(() => {
    if (selectedCounties.length === 0) {
      setCountyKpiData({});
      return;
    }

    // Fetch data for all selected counties
    selectedCounties.forEach(county => {
      fetchCountyKpi(county);
    });
  }, [selectedCounties, minAcres, maxAcres, acresFilterMode, fetchCountyKpi]);

  // Calculate totals across all selected counties
  const totals = React.useMemo(() => {
    const kpiValues = Object.values(countyKpiData);
    if (kpiValues.length === 0) return null;

    return {
      nonEnrichedCount: kpiValues.reduce((sum, k) => sum + (k.nonEnrichedCount || 0), 0),
      enrichedCount: kpiValues.reduce((sum, k) => sum + (k.enrichedCount || 0), 0),
      enrichedWithEmail: kpiValues.reduce((sum, k) => sum + (k.enrichedWithEmail || 0), 0),
      totalEmailSlots: kpiValues.reduce((sum, k) => sum + (k.totalEmailSlots || 0), 0),
      verifiedEmails: kpiValues.reduce((sum, k) => sum + (k.verifiedEmails || 0), 0),
      validEmails: kpiValues.reduce((sum, k) => sum + (k.zbStatusBreakdown?.valid || 0), 0),
      batchDataCost: kpiValues.reduce((sum, k) => sum + ((k.enrichedWithEmail || 0) * batchDataCostPerCall), 0),
      zbVerifiedCost: kpiValues.reduce((sum, k) => sum + ((k.verifiedEmails || 0) * zbCostPerEmail), 0)
    };
  }, [countyKpiData, batchDataCostPerCall, zbCostPerEmail]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-gray-900">Cost Analytics Dashboard</h2>
        <p className="text-sm text-gray-500 mt-1">
          Analyze enrichment costs, email verification expenses, and ROI metrics per county
        </p>
      </div>

      {/* Filters Section */}
      <div className="bg-gray-50 rounded-xl border p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* State Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
            <select
              value={stateId}
              onChange={(e) => setStateId(e.target.value)}
              className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="" disabled>Select a state</option>
              {states.map((s) => (
                <option key={s.state_id} value={s.state_id}>{s.state_name}</option>
              ))}
            </select>
          </div>

          {/* County Multi-Select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Counties {selectedCounties.length > 0 && <span className="text-blue-600">({selectedCounties.length})</span>}
            </label>
            <MultiCountySelect
              counties={counties}
              selectedCounties={selectedCounties}
              onChange={setSelectedCounties}
              disabled={!stateId}
            />
          </div>

          {/* Acres Filter Mode */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Acres Filter</label>
            <select
              value={acresFilterMode}
              onChange={(e) => setAcresFilterMode(e.target.value)}
              className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="atleast">At least</option>
              <option value="atmax">At max</option>
              <option value="between">In between</option>
            </select>
          </div>

          {/* Acres Values */}
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {acresFilterMode === 'atmax' ? 'Max' : 'Min'} Acres
              </label>
              <input
                type="number"
                min={0}
                value={acresFilterMode === 'atmax' ? maxAcres : minAcres}
                onChange={(e) => {
                  const val = e.target.value === '' ? '' : Number(e.target.value);
                  acresFilterMode === 'atmax' ? setMaxAcres(val) : setMinAcres(val || 0);
                }}
                className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {acresFilterMode === 'between' && (
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Acres</label>
                <input
                  type="number"
                  min={minAcres}
                  value={maxAcres}
                  onChange={(e) => setMaxAcres(e.target.value === '' ? '' : Number(e.target.value))}
                  placeholder="No limit"
                  className="w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}
          </div>
        </div>

        {/* Cost Configuration */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Cost Configuration</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">BatchData Cost per Successful Call ($)</label>
              <input
                type="number"
                step="0.0001"
                min={0}
                value={batchDataCostPerCall}
                onChange={(e) => setBatchDataCostPerCall(parseFloat(e.target.value) || 0)}
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">ZeroBounce Cost per Email ($)</label>
              <input
                type="number"
                step="0.0001"
                min={0}
                value={zbCostPerEmail}
                onChange={(e) => setZbCostPerEmail(parseFloat(e.target.value) || 0)}
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Est. Enrichment Success Rate (%)</label>
              <input
                type="number"
                min={0}
                max={100}
                value={zbSuccessRate}
                onChange={(e) => setZbSuccessRate(Math.min(100, Math.max(0, Number(e.target.value) || 0)))}
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Totals Summary (when multiple counties selected) */}
      {selectedCounties.length > 1 && totals && (
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
          <h3 className="text-lg font-semibold mb-4">
            📊 Combined Totals ({selectedCounties.length} Counties)
          </h3>
          {(() => {
            // nonEnrichedCount is the TOTAL pool, enrichedCount is the enriched subset
            const totalInRange = totals.nonEnrichedCount;
            const awaitingEnrichment = Math.max(0, totals.nonEnrichedCount - totals.enrichedCount);
            const enrichmentPct = totalInRange > 0 ? ((totals.enrichedCount / totalInRange) * 100).toFixed(1) : 0;
            
            return (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <div>
                  <div className="text-indigo-200 text-xs">Total In Range</div>
                  <div className="text-2xl font-bold">{totalInRange.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-indigo-200 text-xs">Enriched ({enrichmentPct}%)</div>
                  <div className="text-2xl font-bold">{totals.enrichedCount.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-indigo-200 text-xs">Awaiting Enrichment</div>
                  <div className="text-2xl font-bold text-amber-300">{awaitingEnrichment.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-indigo-200 text-xs">Valid Leads</div>
                  <div className="text-2xl font-bold text-green-300">{totals.validEmails.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-indigo-200 text-xs">BatchData Cost</div>
                  <div className="text-2xl font-bold">${totals.batchDataCost.toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-indigo-200 text-xs">ZeroBounce Cost</div>
                  <div className="text-2xl font-bold">${totals.zbVerifiedCost.toFixed(2)}</div>
                </div>
              </div>
            );
          })()}
          <div className="mt-4 pt-4 border-t border-indigo-400">
            <div className="flex justify-between items-center">
              <span className="text-indigo-200">Total Investment</span>
              <span className="text-3xl font-bold">${(totals.batchDataCost + totals.zbVerifiedCost).toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Per-County KPI Cards */}
      {selectedCounties.length > 0 ? (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {selectedCounties.map(county => (
            <CountyKpiCard
              key={county.county_id}
              countyName={county.county_name}
              stats={countyKpiData[county.county_id]}
              costs={costs}
              isLoading={loadingCounties.has(county.county_id)}
            />
          ))}
        </div>
      ) : (
        <div className="bg-gray-50 rounded-xl border-2 border-dashed border-gray-300 p-12 text-center">
          <div className="text-4xl mb-4">📊</div>
          <h3 className="text-lg font-medium text-gray-700 mb-2">Select Counties to Analyze</h3>
          <p className="text-sm text-gray-500">
            Choose a state and select one or more counties to view cost analytics and KPI metrics.
          </p>
        </div>
      )}
    </div>
  );
};

export default CostAnalyticsPanel;
